import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../utils/TickerTimeout";
import { SOUND_NAME } from "../../sounds";
import { soundManager } from "../../SoundManager";

// ─── Position offset within the game_view slot (tweak as needed) ───
const Y_OFFSET = -38;

/**
 * PotCollectionRespinCounterController
 *
 * Manages the respin counter spine animation for Pot Collection (Zeus Respins).
 * Uses the `respin_counter` spine asset with the following animation names:
 *
 *   Static states:
 *     "counter_full"   — full (3 respins, shown at bonus entry)
 *     "counter_empty"  — empty (0 respins)
 *
 *   Transitions:
 *     "counter_3to2", "counter_2to1", "counter_1to0"  — decrement
 *     "counter_0to1", "counter_0tofull", "counter_1to2", "counter_1to3", "counter_2to3"  — refill
 *
 *   Exit animations:
 *     "counter_transform_0" .. "counter_transform_3"  — dismiss from given state
 *
 * Single public method: playAnimation(respinsLeft)
 *   - Tracks displayed value internally
 *   - Plays transition if available, falls back to static
 *   - Queues animations to prevent clashing
 */
class PotCollectionRespinCounterController {
  constructor(parentContainer, pcPlayCounterRef) {
    this.parentContainer = parentContainer;
    this.counterSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Currently displayed value (what's shown on screen)
    this.displayedValue = null;

    // Play button counter ref — direct imperative control
    this._pcPlayCounterRef = pcPlayCounterRef || null;

    // Animation queuing (array — coalesced on dequeue)
    this._isAnimating = false;
    this._animationQueue = [];
    this._onIdleCallbacks = [];

    // Animation name caches
    this._availableAnimations = null;
    this._hasAnimationCache = new Map();

    // Tracked timeout for post-transition hold
    this._holdTimeout = null;

    this.initialize();
  }

  /**
   * Check if controller is ready for operations
   */
  isReady() {
    return this.counterSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Initialize the counter spine and add to parent container
   */
  initialize() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "respin_counter-skeleton";
      const atlasAlias = "respin_counter-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return;
      }

      this.counterSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!this.counterSpine?.skeleton?.data?.animations || !this.counterSpine?.state) {
        return;
      }

      // Cache available animations
      this._availableAnimations = this.counterSpine.skeleton.data.animations.map(
        (a) => a.name
      );

      // Add to parent container
      this.parentContainer.addChild(this.counterSpine);

      // Position within slot — game_view bone handles X, Y_OFFSET for fine-tuning
      this.counterSpine.position.set(0, Y_OFFSET);

      // Warm up spine structure
      this.warmup();

      this.initialized = true;
    } catch (error) {
    }
  }

  /**
   * Warm up spine by setting initial empty state
   */
  warmup() {
    if (!this.counterSpine?.state) return;

    try {
      // Start at empty state
      const startAnim = this._getStaticName(0) || this._availableAnimations?.[0];
      if (startAnim && this.hasAnimation(startAnim)) {
        this.counterSpine.state.setAnimation(0, startAnim, false);
        this.counterSpine.state.update(0.016);
      }
      this.displayedValue = 0;
      this._syncPlayCounter(0);
    } catch (_) {
      // Warmup is optional
    }
  }

  /**
   * Check if an animation exists in this spine
   */
  hasAnimation(name) {
    if (this._hasAnimationCache.has(name)) {
      return this._hasAnimationCache.get(name);
    }
    const exists = this._availableAnimations?.includes(name) || false;
    this._hasAnimationCache.set(name, exists);
    return exists;
  }

  /**
   * Push value to the play button counter via ref.
   * null = hide, number = show that value.
   * @private
   */
  _syncPlayCounter(value) {
    const ctrl = this._pcPlayCounterRef?.current;
    if (!ctrl) return;
    if (value === null) {
      ctrl.hide();
    } else {
      ctrl.show(value);
    }
  }

  /**
   * Returns a Promise that resolves when the counter is idle (no animation running, queue empty).
   * Resolves immediately if already idle.
   */
  waitForIdle() {
    if (!this._isAnimating && this._animationQueue.length === 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this._onIdleCallbacks.push(resolve);
    });
  }

  /**
   * Play the animation for the given respins value.
   *
   * @param {number} respinsLeft - Current respins remaining (0-3)
   */
  playAnimation(respinsLeft) {
    if (!this.isReady()) return;

    // Same value — nothing to animate
    if (this.displayedValue === respinsLeft && !this._isAnimating) return;

    // If currently animating, push to queue — animation completes naturally,
    // then _processQueue coalesces all pending to the LAST value.
    if (this._isAnimating) {
      this._animationQueue.push(respinsLeft);
      return;
    }

    this._executeAnimation(respinsLeft);
  }

  /**
   * Play the 0→3 transition animation. Used at bonus entry.
   */
  showFull() {
    if (!this.isReady()) return;

    // Clear any pending queue — bonus re-entry resets everything
    this._animationQueue.length = 0;
    this._isAnimating = false;

    try {
      this.counterSpine.state.clearTracks();
      this.counterSpine.state.clearListeners();
      this.counterSpine.state.setAnimation(0, this._getTransitionName(0, 3), false);
      this.displayedValue = 3;
      this._syncPlayCounter(3);
    } catch (_) {
      // Silent fail
    }
  }

  /**
   * Play exit animation from current state.
   * @returns {Promise} Resolves when the out animation completes (or immediately on error).
   */
  playOut() {
    if (!this.isReady()) return Promise.resolve();

    // Clear queue — we're exiting
    this._animationQueue.length = 0;
    this._isAnimating = false;

    // Hide external UI (play button counter) immediately
    const outValue = this.displayedValue ?? 0;
    this.displayedValue = null;
    this._syncPlayCounter(null);

    const outAnim = this._getOutName(outValue);
    if (this.hasAnimation(outAnim)) {
      return new Promise((resolve) => {
        try {
          this.counterSpine.state.clearTracks();
          this.counterSpine.state.clearListeners();
          this.counterSpine.state.setAnimation(0, outAnim, false);
          this.counterSpine.state.addListener({
            complete: (entry) => {
              if (entry.animation.name === outAnim) {
                resolve();
              }
            },
          });
        } catch (_) {
          resolve();
        }
      });
    }
    return Promise.resolve();
  }

  /**
   * Execute an animation immediately
   * @private
   */
  _executeAnimation(respinsLeft) {
    const from = this.displayedValue;

    // First call or no previous value — show static
    if (from === null || from === undefined) {
      this._showStatic(respinsLeft, true);
      this.displayedValue = respinsLeft;
      this._processQueue();
      return;
    }

    // Same as current — skip, process next
    if (from === respinsLeft) {
      this._processQueue();
      return;
    }

    // Try transition animation
    const animName = this._getTransitionName(from, respinsLeft);

    if (this.hasAnimation(animName)) {
      this._isAnimating = true;
      this._playTransitionAnim(animName, respinsLeft);
    } else {
      this._showStatic(respinsLeft, false);
      this._processQueue();
    }

    this.displayedValue = respinsLeft;
    this._syncPlayCounter(respinsLeft);
  }

  /**
   * Process queued animations after current one finishes.
   * Coalesces: drains the entire queue and jumps to the LAST value.
   * This way animations play fully but the counter never falls behind.
   * @private
   */
  _processQueue() {
    this._isAnimating = false;

    if (this._animationQueue.length > 0) {
      // Coalesce: take only the last value, skip intermediates
      const lastValue = this._animationQueue[this._animationQueue.length - 1];
      this._animationQueue.length = 0;
      this._executeAnimation(lastValue);
    } else {
      // Fire all idle waiters
      const cbs = this._onIdleCallbacks.splice(0);
      for (const cb of cbs) cb();
    }
  }

  /**
   * Play a named transition animation
   * @private
   */
  _playTransitionAnim(animName, targetValue) {
    try {
      this.counterSpine.state.clearTracks();
      this.counterSpine.state.clearListeners();
      this.counterSpine.state.setAnimation(0, animName, false);

      soundManager.play(SOUND_NAME.respins_counter);

      this.counterSpine.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === animName) {
            // Show static state so the counter visually settles on the number
            this._showStatic(targetValue, true);

            // Hold briefly so the player can see the settled value
            // before the next spin fires (prevents rapid-fire appearance)
            // 100ms — tuned for the 167ms (5-frame) spine animations
            this._holdTimeout = new TickerTimeout(() => {
              this._holdTimeout = null;
              this._processQueue();
            }, 100);
          }
        },
      });
    } catch (_) {
      this._showStatic(targetValue, false);
      this._processQueue();
    }
  }

  /**
   * Show a static counter state
   * @private
   */
  _showStatic(value, silent = false) {
    if (!this.isReady()) return;

    const animName = this._getStaticName(value);
    if (!animName || !this.hasAnimation(animName)) return;

    try {
      this.counterSpine.state.clearTracks();
      this.counterSpine.state.clearListeners();
      this.counterSpine.state.setAnimation(0, animName, false);

      if (!silent) {
        soundManager.play(SOUND_NAME.respins_counter);
      }
    } catch (_) {
      // Silent fail
    }
  }

  // ── Animation name helpers ────────────────────────────────────────────────

  /**
   * Get transition animation name: "counter_FROMtoTO"
   * Special case: 0→3 = "counter_0tofull"
   * @private
   */
  _getTransitionName(from, to) {
    if (from === 0 && to === 3) return "counter_0tofull";
    return `counter_${from}to${to}`;
  }

  /**
   * Get static state animation name
   * @private
   */
  _getStaticName(value) {
    if (value === 0) return "counter_empty";
    if (value === 3) return "counter_full";
    return null; // No static for intermediate values
  }

  /**
   * Get exit animation name: "counter_transform_N"
   * @private
   */
  _getOutName(value) {
    return `counter_transform_${value}`;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.isCleanedUp) return;
    this.isCleanedUp = true;

    if (this.counterSpine) {
      this.counterSpine.state?.clearListeners();
      this.counterSpine.state?.clearTracks();

      if (this.parentContainer?.children?.includes(this.counterSpine)) {
        this.parentContainer.removeChild(this.counterSpine);
      }
      this.counterSpine.destroy();
      this.counterSpine = null;
    }

    this._availableAnimations = null;
    this._hasAnimationCache.clear();
    this.displayedValue = null;
    this.initialized = false;

    // Clear any pending hold timeout
    if (this._holdTimeout) {
      this._holdTimeout.clear();
      this._holdTimeout = null;
    }

    // Flush idle waiters so they don't hang
    const cbs = this._onIdleCallbacks.splice(0);
    for (const cb of cbs) cb();
    this._animationQueue.length = 0;
  }
}

export default PotCollectionRespinCounterController;
